import { useEffect, useState } from "react";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);

  const load = () => api.get("/api/notifications").then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const markAll = async () => {
    await api.post("/api/notifications/read-all").catch(() => {});
    load();
  };

  const markOne = async (id) => {
    await api.post(`/api/notifications/${id}/read`).catch(() => {});
    load();
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button className="relative rounded-full p-2 text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-800" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {data.unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-emerald-700 px-1 text-[10px] font-bold text-white" data-testid="notification-unread-count">
              {data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <p className="font-display text-sm font-bold text-slate-900">Notifications</p>
          {data.unread > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={markAll} data-testid="notification-mark-all">
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length ? (
            data.items.map((n) => (
              <button
                key={n.id}
                onClick={() => markOne(n.id)}
                data-testid={`notification-item-${n.id}`}
                className={`flex w-full gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${
                  n.read ? "opacity-60" : ""
                }`}
              >
                <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-slate-300" : "bg-emerald-600"}`} />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-slate-900">{n.title}</span>
                  <span className="mt-0.5 block text-xs text-slate-600">{n.body}</span>
                  <span className="mt-1 block text-[11px] text-slate-400">{new Date(n.created_at).toLocaleString()}</span>
                </span>
                {n.read && <Check className="mt-1 h-3.5 w-3.5 shrink-0 text-slate-300" />}
              </button>
            ))
          ) : (
            <p className="px-4 py-8 text-center text-sm text-slate-500" data-testid="notification-empty">
              No notifications yet.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
